// frontend/src/pages/AcceptWriterInvite.tsx
// Phase 6c: Landing page for Family & Friends memorial writer invitees.
//
// Flow:
//   1. Invitee clicks the email link → /accept-writer-invite?token={uuid}
//   2. If not signed in, we surface a "Sign in with the email that received
//      this invitation" card and bounce them through Clerk with a redirect
//      back here so they land on the same URL post-sign-in.
//   3. Once signed in, we POST the token to /api/memorial-writers/accept
//      and render success / error states.
//
// Error handling is tied to the exact shape of api/memorial-writers/accept:
//   - email_mismatch (403)  → "this invite was sent to a different email"
//   - already_accepted (409) → "someone else already claimed this invite"
//   - license_archived / license_finalised (400) → piece is frozen
//   - 404 → invitation not found (expired / deleted / wrong token)
//
// The idempotent success path (201's `alreadyAccepted:true`) resolves to the
// same success UI as a fresh accept — retries and double-clicks are safe.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useUser, useAuth } from '@clerk/clerk-react';
import Header from '../components/Header';
import Footer from '../components/Footer';

type Status = 'idle' | 'loading' | 'success' | 'error' | 'needs_sign_in';

interface AcceptSuccess {
  success: true;
  alreadyAccepted?: boolean;
  licenseId: string;
  deceasedName: string | null;
  tier: 'single_tribute' | 'family_memorial' | null;
}

interface AcceptError {
  success: false;
  error: string;
  message?: string;
}

const AcceptWriterInvite: React.FC = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const { isLoaded, isSignedIn } = useUser();
  const { getToken } = useAuth();
  const navigate = useNavigate();

  const [status, setStatus] = useState<Status>('idle');
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [result, setResult] = useState<AcceptSuccess | null>(null);
  const attemptedRef = useRef(false);

  useEffect(() => {
    document.title = 'Cyrano — Accept Writer Invitation';
    window.scrollTo(0, 0);
  }, []);

  // Redirect URL to preserve this page's location after sign-in.
  const signInRedirect = useMemo(() => {
    const here = `/accept-writer-invite?token=${encodeURIComponent(token)}`;
    return `/sign-in?redirect_url=${encodeURIComponent(here)}`;
  }, [token]);

  const signUpRedirect = useMemo(() => {
    const here = `/accept-writer-invite?token=${encodeURIComponent(token)}`;
    return `/sign-up?redirect_url=${encodeURIComponent(here)}`;
  }, [token]);

  const claimInvite = useCallback(async () => {
    if (!token) {
      setErrorCode('missing_token');
      setErrorMessage('This invitation link is missing its token. Please open the link from your email again.');
      setStatus('error');
      return;
    }

    setStatus('loading');
    setErrorCode(null);
    setErrorMessage(null);

    try {
      const authToken = await getToken();
      if (!authToken) {
        setStatus('needs_sign_in');
        return;
      }

      const resp = await fetch('/api/memorial-writers/accept', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token }),
      });

      const data = await resp.json().catch(() => ({} as any));

      if (resp.ok && data?.success) {
        setResult(data as AcceptSuccess);
        setStatus('success');
        return;
      }

      const errPayload = data as AcceptError;
      setErrorCode(errPayload?.error || `http_${resp.status}`);
      setErrorMessage(errPayload?.message || null);
      setStatus('error');
    } catch (err: any) {
      console.error('Accept writer invite failed:', err);
      setErrorCode('network_error');
      setErrorMessage('We couldn\u2019t reach the server. Please check your connection and try again.');
      setStatus('error');
    }
  }, [token, getToken]);

  // Auto-claim once Clerk has loaded and the user is signed in. We ref-guard
  // so strict-mode double-effect doesn't fire the POST twice.
  useEffect(() => {
    if (!isLoaded) return;

    if (!token) {
      setErrorCode('missing_token');
      setErrorMessage('This invitation link is missing its token. Please open the link from your email again.');
      setStatus('error');
      return;
    }

    if (!isSignedIn) {
      setStatus('needs_sign_in');
      return;
    }

    if (attemptedRef.current) return;
    attemptedRef.current = true;
    claimInvite();
  }, [isLoaded, isSignedIn, token, claimInvite]);

  const openDashboard = () => navigate('/dashboard');
  const openPiece = () => {
    // Deep-link preference is to resume into the conversation if one exists.
    // For now we send them to the Dashboard, which already surfaces shared-
    // with-me conversations; the frontend can deep-link once the conversation
    // id is flowing through the accept response (future enhancement).
    navigate('/dashboard');
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', backgroundColor: '#eae8e4' }}>
      <Header />
      <main style={{ flex: 1, display: 'flex', justifyContent: 'center', padding: '48px 20px' }}>
        <div
          style={{
            width: '100%',
            maxWidth: '560px',
            backgroundColor: '#ffffff',
            borderRadius: '16px',
            padding: '40px 32px',
            boxShadow: '0 4px 24px rgba(25, 40, 92, 0.08)',
            border: '1px solid rgba(25, 40, 92, 0.06)',
          }}
        >
          {/* HEADER — always visible regardless of state */}
          <div style={{ textAlign: 'center', marginBottom: '28px' }}>
            <h1
              style={{
                fontFamily: 'Playfair Display, Georgia, serif',
                fontSize: '28px',
                fontWeight: 600,
                color: '#19285c',
                margin: '0 0 8px 0',
                lineHeight: 1.3,
              }}
            >
              {status === 'success' ? 'Welcome to the circle' : 'Family &amp; Friends Invitation'}
            </h1>
            <p style={{ fontSize: '15px', color: '#8c83b4', margin: 0 }}>
              {status === 'success'
                ? 'You now have access to co-write this memorial piece.'
                : 'A family member has invited you to help write a memorial tribute together.'}
            </p>
          </div>

          {/* LOADING */}
          {status === 'loading' && (
            <div style={{ textAlign: 'center', padding: '24px 0' }}>
              <div
                style={{
                  width: '40px',
                  height: '40px',
                  border: '3px solid #eae8e4',
                  borderTopColor: '#19285c',
                  borderRadius: '50%',
                  animation: 'spin 0.9s linear infinite',
                  margin: '0 auto 16px',
                }}
              />
              <p style={{ fontSize: '15px', color: '#19285c', margin: 0 }}>Checking your invitation\u2026</p>
              <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
            </div>
          )}

          {/* NEEDS SIGN IN */}
          {status === 'needs_sign_in' && (
            <div>
              <p style={{ fontSize: '15px', lineHeight: 1.6, color: '#131e40', marginTop: 0 }}>
                To accept this invitation, please sign in with the email address that received it.
                If you don\u2019t have a Cyrano account yet, you can create one in a minute \u2014 we\u2019ll bring you
                right back here to finish accepting.
              </p>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '24px' }}>
                <a
                  href={signInRedirect}
                  style={{
                    display: 'block',
                    backgroundColor: '#19285c',
                    color: '#ffffff',
                    textDecoration: 'none',
                    padding: '14px 20px',
                    borderRadius: '10px',
                    fontSize: '15px',
                    fontWeight: 600,
                    textAlign: 'center',
                    minHeight: '44px',
                    boxSizing: 'border-box',
                  }}
                >
                  Sign in to accept
                </a>
                <a
                  href={signUpRedirect}
                  style={{
                    display: 'block',
                    backgroundColor: '#ffffff',
                    color: '#19285c',
                    textDecoration: 'none',
                    padding: '14px 20px',
                    borderRadius: '10px',
                    fontSize: '15px',
                    fontWeight: 600,
                    textAlign: 'center',
                    border: '1.5px solid #19285c',
                    minHeight: '44px',
                    boxSizing: 'border-box',
                  }}
                >
                  Create a free account
                </a>
              </div>

              <p
                style={{
                  fontSize: '13px',
                  color: '#8c83b4',
                  marginTop: '24px',
                  marginBottom: 0,
                  textAlign: 'center',
                  lineHeight: 1.5,
                }}
              >
                There\u2019s no charge to be a writer. The person who invited you has already taken care of
                the Family &amp; Friends Group Memorial subscription.
              </p>
            </div>
          )}

          {/* SUCCESS */}
          {status === 'success' && result && (
            <div>
              <p style={{ fontSize: '15px', lineHeight: 1.6, color: '#131e40', marginTop: 0 }}>
                {result.alreadyAccepted
                  ? 'You\u2019ve already accepted this invitation \u2014 you\u2019re all set.'
                  : 'Your invitation has been accepted.'}
                {result.deceasedName ? (
                  <>
                    {' '}You now have access to the memorial piece for{' '}
                    <strong style={{ color: '#19285c' }}>{result.deceasedName}</strong>. You can read
                    the owner\u2019s eulogy, write your own tribute, and contribute alongside other family
                    and friends.
                  </>
                ) : (
                  <> You now have access to this memorial piece.</>
                )}
              </p>

              <div
                style={{
                  backgroundColor: '#eae8e4',
                  borderRadius: '10px',
                  padding: '16px 18px',
                  margin: '24px 0 20px',
                  borderLeft: '3px solid #d4adbb',
                }}
              >
                <p
                  style={{
                    fontSize: '13px',
                    color: '#8c83b4',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.04em',
                    margin: '0 0 6px 0',
                  }}
                >
                  What happens next
                </p>
                <p style={{ fontSize: '14px', color: '#131e40', margin: 0, lineHeight: 1.55 }}>
                  Head to your Dashboard to see the shared piece. From there you can start your own
                  tribute, read what others have written, or pick up where the owner left off.
                </p>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <button
                  type="button"
                  onClick={openPiece}
                  style={{
                    backgroundColor: '#19285c',
                    color: '#ffffff',
                    padding: '14px 20px',
                    borderRadius: '10px',
                    fontSize: '15px',
                    fontWeight: 600,
                    border: 'none',
                    cursor: 'pointer',
                    minHeight: '44px',
                  }}
                >
                  Open my Dashboard
                </button>
              </div>
            </div>
          )}

          {/* ERROR */}
          {status === 'error' && (
            <div>
              <p style={{ fontSize: '15px', lineHeight: 1.6, color: '#131e40', marginTop: 0 }}>
                {errorMessage || errorCopyFor(errorCode)}
              </p>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '24px' }}>
                {errorCode === 'email_mismatch' && (
                  <a
                    href="/sign-out"
                    style={{
                      display: 'block',
                      backgroundColor: '#19285c',
                      color: '#ffffff',
                      textDecoration: 'none',
                      padding: '14px 20px',
                      borderRadius: '10px',
                      fontSize: '15px',
                      fontWeight: 600,
                      textAlign: 'center',
                      minHeight: '44px',
                      boxSizing: 'border-box',
                    }}
                  >
                    Sign out &amp; try a different email
                  </a>
                )}

                {errorCode !== 'email_mismatch' && (
                  <button
                    type="button"
                    onClick={() => {
                      attemptedRef.current = false;
                      claimInvite();
                    }}
                    style={{
                      backgroundColor: '#19285c',
                      color: '#ffffff',
                      padding: '14px 20px',
                      borderRadius: '10px',
                      fontSize: '15px',
                      fontWeight: 600,
                      border: 'none',
                      cursor: 'pointer',
                      minHeight: '44px',
                    }}
                  >
                    Try again
                  </button>
                )}

                <button
                  type="button"
                  onClick={openDashboard}
                  style={{
                    backgroundColor: '#ffffff',
                    color: '#19285c',
                    padding: '14px 20px',
                    borderRadius: '10px',
                    fontSize: '15px',
                    fontWeight: 600,
                    border: '1.5px solid #19285c',
                    cursor: 'pointer',
                    minHeight: '44px',
                  }}
                >
                  Go to Dashboard
                </button>
              </div>

              <p
                style={{
                  fontSize: '13px',
                  color: '#8c83b4',
                  marginTop: '20px',
                  marginBottom: 0,
                  textAlign: 'center',
                  lineHeight: 1.5,
                }}
              >
                Need help? Email{' '}
                <a href="mailto:support@cyrano.cloud" style={{ color: '#19285c' }}>
                  support@cyrano.cloud
                </a>{' '}
                and we\u2019ll sort it out.
              </p>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
};

function errorCopyFor(code: string | null): string {
  switch (code) {
    case 'missing_token':
      return 'This invitation link is missing its token. Please open the link from your email again.';
    case 'email_mismatch':
      return 'This invitation was sent to a different email address. Please sign in with the email that received the invitation.';
    case 'already_accepted':
      return 'This invitation has already been accepted by someone else. If you think that\u2019s a mistake, please contact the person who invited you.';
    case 'license_archived':
      return 'The 90-day writing window for this memorial piece has closed. The invitation is no longer valid.';
    case 'license_finalised':
      return 'This memorial piece has been finalised. Accepting the invitation is no longer available.';
    case 'http_404':
      return 'We couldn\u2019t find this invitation. The link may have expired or been revoked.';
    case 'http_401':
      return 'Your session has expired. Please sign in again to accept the invitation.';
    case 'network_error':
      return 'We couldn\u2019t reach the server. Please check your connection and try again.';
    default:
      return 'We couldn\u2019t accept this invitation right now. Please try again, or contact support if the problem continues.';
  }
}

export default AcceptWriterInvite;
